/*
  Hides product cards tagged `hide_<mode>_<market>` (e.g. `hide_vertical_italia`)
  wherever they appear on the storefront — collection/PLP grids, native search
  and predictive search, recommendation/complementary carousels, and
  third-party widgets we don't render ourselves (Kimera's search overlay and
  its recommendation carousel), based only on the *current* display mode and
  market. Scalable by construction: a new tag like `hide_default_francia`
  works immediately, with no code change here — this file only ever pattern-
  matches `hide_<mode>_<market>` against whatever mode/market are active now,
  it never hardcodes either.

  Mode: 'vertical' when <html> has the display-vertical class set by
  snippets/vertical-resources.liquid (kiosk/pantalla mode), 'default'
  otherwise — mirrors that same convention exactly.

  Market: parsed from the `market-<handle>` class layout/theme.liquid already
  puts on <body> (e.g. `market-italia`) — no new signal needed.

  Two complementary mechanisms:
  1. Our own cards: snippets/product-hide-tags.liquid renders this product's
     matching tags, verbatim, into a `data-hide-tags` attribute on the card's
     own wrapper at render time. Cheap, synchronous, no network request.
  2. Cards we don't render (Kimera's search results / recommendation
     carousel, or any future third-party widget): resolved lazily per product
     handle via Shopify's public `/products/<handle>.js` endpoint the first
     time a given handle is seen, then cached — further appearances of the
     same product (another carousel, a second search) cost nothing.

  Runs on load and on every DOM mutation (MutationObserver), so it catches
  content injected later: Kimera's results, any AJAX-rendered carousel, a
  reopened cart drawer, etc.
*/
(function () {
  function currentMode() {
    return document.documentElement.classList.contains('display-vertical') ? 'vertical' : 'default';
  }

  function currentMarket() {
    var match = document.body.className.match(/(?:^|\s)market-(\S+)/);
    return match ? match[1] : null;
  }

  function hideKey() {
    var market = currentMarket();
    if (!market) return null;
    return 'hide_' + currentMode() + '_' + market;
  }

  // Element nodes added by a mutation can themselves be the match (e.g. a
  // whole card inserted as one node, not just a descendant of what was
  // inserted) — querySelectorAll alone would miss that, so every scan here
  // also checks the root node itself when it's an element.
  function queryIncludingSelf(root, selector) {
    var results = root.nodeType === 1 && root.matches(selector) ? [root] : [];
    if (root.querySelectorAll) {
      results = results.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
    }
    return results;
  }

  // ── Our own cards: data-hide-tags already has the answer ──────────────────
  function applyOwnCards(root) {
    var key = hideKey();
    if (!key) return;
    queryIncludingSelf(root || document, '[data-hide-tags]').forEach(function (el) {
      var tags = el.getAttribute('data-hide-tags').split(',');
      el.hidden = tags.indexOf(key) !== -1;
    });
  }

  // ── Cards we don't render: resolve by handle, once per handle ────────────
  var externalTagsCache = new Map(); // handle -> tags array (or null while pending)

  function applyExternalCard(link) {
    var key = hideKey();
    if (!key) return;
    var match = (link.getAttribute('href') || '').match(/\/products\/([a-zA-Z0-9_-]+)/);
    if (!match) return;
    var handle = match[1];

    // The whole visual card, not just the link — Kimera's result/carousel
    // item is presumed to be the nearest ancestor that looks like a card;
    // falls back to the link itself if nothing more specific is found.
    var card = link.closest('[class*="card"], [class*="item"], li, article') || link;

    function decide(tags) {
      if (tags && tags.indexOf(key) !== -1) card.hidden = true;
    }

    if (externalTagsCache.has(handle)) {
      var cached = externalTagsCache.get(handle);
      if (cached && typeof cached.then === 'function') {
        cached.then(decide); // request already in flight for this handle
      } else {
        decide(cached);
      }
      return;
    }

    var request = fetch('/products/' + handle + '.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var tags = data ? data.tags : null;
        externalTagsCache.set(handle, tags);
        decide(tags);
        return tags;
      })
      .catch(function () { externalTagsCache.delete(handle); });

    externalTagsCache.set(handle, request);
  }

  function scan(root) {
    var scope = root || document;
    applyOwnCards(scope);
    queryIncludingSelf(scope, 'a[href*="/products/"]').forEach(function (link) {
      // Already covered by data-hide-tags above — don't double-handle it.
      if (link.closest('[data-hide-tags]')) return;
      applyExternalCard(link);
    });
  }

  scan();

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return; // element nodes only
        scan(node);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
